/**
 * RepeatableRows — shared behaviour + chrome for the AI intake wizard's repeating
 * row editors (Step 4 expected outcomes, Step 5 data-field inventory).
 *
 * In the FAIR app each of those steps re-implemented the same three rules inline.
 * They are reproduced here once:
 *
 *   1. A blank row always renders when the stored list is empty — and is written back to
 *      the use case from an effect, never during render (the source's comment "never call
 *      update() during render" is load-bearing: the wizard shell holds the use case in
 *      state, so a render-time update loops).
 *   2. Adding fills the trailing blank row when there is one instead of appending a
 *      second empty row. Seeded adds (Step 4's "Try:" hint chips) therefore land in
 *      the row the user is already looking at.
 *   3. The list never drops to zero rows — the remove control is withheld while a
 *      single row remains, so step validation can rely on `length > 0`.
 */
import React, { useEffect, useRef, useState } from 'react';

/** Every repeatable row carries a client-side id used as its React key. */
export interface Identified {
  id: string;
}

/** Row id generator — matches the `crypto.randomUUID()` use elsewhere in Nimbus. */
export const newRowId = (): string => crypto.randomUUID();

interface UseRepeatableRowsArgs<T extends Identified> {
  /** The rows as stored on the use case. May be undefined/empty on a fresh draft. */
  stored: T[] | undefined;
  /** Factory for an empty row. */
  makeBlank: () => T;
  /** True when a row holds no user input yet — drives rule 2. */
  isBlank: (row: T) => boolean;
  /** Writes the new list back to the use case. */
  onChange: (rows: T[]) => void;
  /**
   * Whether an empty stored list should write a blank placeholder back via `onChange` at all -
   * defaults to true, matching every caller before this flag existed (the AI intake wizard's
   * Step 4/5, which genuinely require at least one row for their own step validation to pass).
   *
   * Set to false for a field that is legitimately allowed to be empty and round-trips through a
   * save that DROPS blank entries server-side (e.g. `normalise_transfers` in
   * prod-nimbus-processing-activities - "a transfer with no country is not a record of
   * anything"). With this left true there, the sequence was: mount seeds one blank row (real
   * write, dirties the form) -> Save sends it -> the server strips it as blank and returns the
   * list empty again -> that empty response makes the list "arrive empty" again, exactly the
   * condition this effect re-seeds on -> another blank row gets written back -> dirty again,
   * immediately after the save that was supposed to clear it. Confirmed live: Processing
   * Activities' Transfers list, which most activities legitimately leave empty, made the
   * "unsaved changes" guard in front of every status transition permanently true no matter how
   * many times the record was saved. Rendering is unaffected either way - `rows` below already
   * shows one blank row for an empty list regardless of whether it's written back to the
   * caller's own state.
   */
  seedWhenEmpty?: boolean;
}

export interface RepeatableRowsApi<T> {
  /** The rows to render — never empty. */
  rows: T[];
  /** Fill the trailing blank row with `seed`, or append a new row when none is blank. */
  add: (seed?: Partial<T>) => void;
  /** False while the trailing row is still blank — there is nothing to add yet. */
  canAdd: boolean;
  /** Patch one row by id. */
  patch: (id: string, changes: Partial<T>) => void;
  /** Remove one row by id. No-op while a single row remains (rule 3). */
  remove: (id: string) => void;
  /** Whether a remove control should be offered at all (i.e. more than one row). */
  canRemove: boolean;
}

export function useRepeatableRows<T extends Identified>({
  stored,
  makeBlank,
  isBlank,
  onChange,
  seedWhenEmpty = true,
}: UseRepeatableRowsArgs<T>): RepeatableRowsApi<T> {
  const list = stored ?? [];

  // The blank row shown for an empty list must keep one identity for as long as the step is
  // mounted: a fresh id each render would change the row's React key and remount its inputs
  // mid-keystroke (the source minted a uuid during render and had exactly that bug).
  // useState's lazy initialiser runs once, which gives a stable row without reading a ref
  // during render.
  const [placeholder] = useState<T>(makeBlank);
  const rows: T[] = list.length > 0 ? list : [placeholder];

  // Always the latest onChange, read without making the effect below re-run just because its
  // caller re-rendered. Confirmed live as a real bug: every caller passes an inline arrow
  // (`onChange: (rows) => update({ transfers: rows })`), a fresh function identity every
  // render, not memoised. With `onChange` itself in the effect's own dependency array, an
  // empty stored list (a genuinely common case — e.g. a Processing Activity with no international
  // transfers) fired this effect on EVERY render, not once — including the render right after a
  // save's own onSuccess reset `dirty` to false, immediately flipping it back to true. That made
  // the "unsaved changes" guard in front of every status transition permanently true, no matter
  // how many times the record was actually saved.
  const onChangeRef = useRef(onChange);
  // Not assigned directly in the render body - React's own effect-ordering guarantee (effects
  // in one component run in declaration order within a commit) is what makes this safe: this
  // one is declared first, so the ref is always current by the time the effect below reads it.
  useEffect(() => {
    onChangeRef.current = onChange;
  });

  // Materialise that row into the use case, so step validation counts what is on screen.
  // Writing the very same object keeps the key stable across the transition. Depends only on
  // `list.length`/`placeholder` (both stable while the list stays empty) so it fires once per
  // genuine empty-to-empty transition, not once per render. Skipped entirely when
  // `seedWhenEmpty` is false - see its own doc comment above.
  useEffect(() => {
    if (seedWhenEmpty && list.length === 0) onChangeRef.current([placeholder]);
  }, [list.length, placeholder, seedWhenEmpty]);

  const trailing = rows[rows.length - 1];
  const trailingIsBlank = !!trailing && isBlank(trailing);

  const add = (seed?: Partial<T>) => {
    if (trailingIsBlank) {
      if (!seed) return; // nothing to add — a blank row is already on screen
      onChange(rows.map(r => (r.id === trailing.id ? ({ ...r, ...seed } as T) : r)));
      return;
    }
    onChange([...rows, { ...makeBlank(), ...seed } as T]);
  };

  const patch = (id: string, changes: Partial<T>) => {
    onChange(rows.map(r => (r.id === id ? ({ ...r, ...changes } as T) : r)));
  };

  const remove = (id: string) => {
    if (rows.length <= 1) return;
    onChange(rows.filter(r => r.id !== id));
  };

  return { rows, add, canAdd: !trailingIsBlank, patch, remove, canRemove: rows.length > 1 };
}

/**
 * Border accents a row may take. These are the Nimbus tone names, so a row can be
 * accented straight from a domain tone map (e.g. a minute entry's category tone)
 * without a translation table.
 */
export type RowAccent = 'primary' | 'success' | 'warning' | 'danger' | 'info' | 'neutral';

interface RepeatableRowCardProps {
  /** Noun for the row, e.g. "Outcome" — rendered as "Outcome 1". */
  label: string;
  index: number;
  /** Optional border accent reflecting the row's state (e.g. personal data present). */
  accent?: RowAccent;
  /** Status chips rendered beside the row number. */
  chips?: React.ReactNode;
  /** Supplied only when the row may be removed. */
  onRemove?: () => void;
  children: React.ReactNode;
}

/** The row shell: number, status chips, remove control, then the row's own fields. */
export const RepeatableRowCard: React.FC<RepeatableRowCardProps> = ({
  label,
  index,
  accent,
  chips,
  onRemove,
  children,
}) => (
  <div className={`np-row-card${accent ? ` np-row-card-accent-${accent}` : ''}`}>
    <div className="np-row-head">
      <span className="np-row-num">
        {label} {index + 1}
      </span>
      {chips}
      {onRemove && (
        <button
          type="button"
          className="np-icon-btn np-icon-btn-danger"
          onClick={onRemove}
          title={`Remove ${label.toLowerCase()} ${index + 1}`}
          aria-label={`Remove ${label.toLowerCase()} ${index + 1}`}
        >
          <i className="ri-delete-bin-line"></i>
        </button>
      )}
    </div>
    {children}
  </div>
);
